import React, { useContext } from "react";
import { useNavigate } from "react-router-dom";
import { AthleteContext } from "./App";

function buildStravaUrl() {
  // Random state stored in sessionStorage, verified in Home.jsx to prevent login-CSRF
  const state = Array.from(crypto.getRandomValues(new Uint8Array(16)))
    .map(b => b.toString(16).padStart(2, "0"))
    .join("");
  sessionStorage.setItem("oauthState", state);

  const params = new URLSearchParams({
    client_id: process.env.REACT_APP_STRAVA_CLIENT_ID,
    response_type: "code",
    redirect_uri: `${process.env.REACT_APP_FRONTEND_URL}/`,
    scope: "read,activity:read_all",
    state
  });
  return `https://www.strava.com/oauth/authorize?${params}`;
}

export default function SignupOrLogin() {
  const { currentAthlete } = useContext(AthleteContext);
  const navigate = useNavigate();

  if (currentAthlete) {
    return (
      <div className="pane pane-center dark_pane">
        <h1>Je bent al aangemeld</h1>
        <p>
          Je Strava-account is al gekoppeld als{" "}
          <strong>{currentAthlete.firstName} {currentAthlete.lastName}</strong>.
        </p>
        <button className="btn-primary" onClick={() => navigate("/onboarding")}>
          Ga naar mijn instellingen
        </button>
      </div>
    );
  }

  return (
    <div className="pane pane-center dark_pane">
      <h1>Aanmelden</h1>
      <p>
        Wil je graag deelnemen aan de actie? Meld je dan aan. De aanmelding verloopt via Strava,
        zodat we jouw activiteiten kunnen volgen en tonen op onze website.
        Je hebt dus een Strava-account nodig. Na aanmelding kun je jouw favoriete routes delen
        en zien welke routes anderen delen. Doe mee en steun het goede doel!
      </p>

      <h2>Hoe werkt het?</h2>
      <ol className="signup-steps">
        <li>
          <strong>Strava-account</strong>
          <p>
            Je hebt een account op{" "}
            <a href="https://www.strava.com" target="_blank" rel="noreferrer">strava.com</a>{" "}
            nodig om jouw sportactiviteiten bij te houden. Heb je er nog geen? Maak er gratis een aan.
          </p>
        </li>
        <li>
          <strong>Koppel jouw Strava-account</strong>
          <p>
            Klik op de knop hieronder. Je wordt doorgestuurd naar Strava en gevraagd
            toestemming te geven om jouw activiteiten te delen met Hartekind actie 2026.
          </p>
        </li>
        <li>
          <strong>Stel jouw profiel in</strong>
          <p>
            Na het koppelen stel je jouw weergavenaam en activiteitsfilters in.
            Met de filters bepaal je welke Strava-activiteiten zichtbaar zijn op de kaart
            (bijv. activiteiten met &ldquo;Hartekind&rdquo; in de naam).
          </p>
        </li>
        <li>
          <strong>Wacht op goedkeuring</strong>
          <p>
            De beheerder controleert je aanmelding en keurt die goed. Daarna worden
            jouw routes gesynchroniseerd en zichtbaar op de kaart.
          </p>
        </li>
      </ol>

      <a href={buildStravaUrl()} className="btn-strava">
        <svg width="148" height="30" viewBox="0 0 237 48" fill="none" xmlns="http://www.w3.org/2000/svg">
          <rect width="236.867" height="48" rx="6" fill="#FC5200" />
          <path fillRule="evenodd" clipRule="evenodd" d="M180.749 31.8195L180.748 31.8188H185.357L188.188 26.1268L191.019 31.8188H196.618L188.187 15.5403L180.184 30.9945L177.111 26.5078C179.008 25.5928 180.191 24.0081 180.191 21.7318V21.687C180.191 20.0803 179.7 18.9197 178.763 17.9822C177.669 16.8887 175.906 16.1968 173.139 16.1968H165.506V31.8195H170.728V27.3558H171.844L174.79 31.8195H180.749ZM212.954 15.5403L204.524 31.8188H210.124L212.955 26.1268L215.786 31.8188H221.385L212.954 15.5403ZM200.576 32.4593L209.006 16.1808H203.406L200.575 21.8729L197.744 16.1808H192.144L200.576 32.4593ZM172.982 23.6287C174.232 23.6287 174.991 23.0708 174.991 22.1112V22.0663C174.991 21.0621 174.21 20.5711 173.005 20.5711H170.728V23.6287H172.982ZM154.337 20.6158H149.74V16.1968H164.157V20.6158H159.56V31.8195H154.337V20.6158ZM137.015 26.1507L134.225 29.4761C136.211 31.2172 139.068 32.1097 142.237 32.1097C146.433 32.1097 149.133 30.101 149.133 26.82V26.7756C149.133 23.6287 146.455 22.468 142.46 21.7318C140.808 21.419 140.384 21.1515 140.384 20.7273V20.6827C140.384 20.3033 140.742 20.0355 141.523 20.0355C142.973 20.0355 144.737 20.5042 146.209 21.5754L148.754 18.0493C146.946 16.6209 144.714 15.9065 141.701 15.9065C137.394 15.9065 135.073 18.2055 135.073 21.1737V21.2185C135.073 24.5214 138.153 25.526 141.656 26.2398C143.33 26.5747 143.821 26.82 143.821 27.2665V27.3113C143.821 27.7352 143.42 27.9805 142.482 27.9805C140.652 27.9805 138.711 27.4452 137.015 26.1507Z" fill="white" />
          <path d="M117.92 31.6812V21.9622H119.919V25.7533H124.137V21.9622H126.136V31.6812H124.137V27.5179H119.919V31.6812H117.92Z" fill="white" />
          <path d="M110.959 31.6812V23.713H107.844V21.9622H116.088V23.713H112.958V31.6812H110.959Z" fill="white" />
          <path d="M104.02 31.6812V21.9622H106.018V31.6812H104.02Z" fill="white" />
          <path d="M92.1013 31.6812L89.5371 21.9622H91.6464L92.7492 27.2008C92.8871 27.9039 93.0112 28.4691 93.1352 29.3376H93.1904C93.3282 28.607 93.4109 28.152 93.6315 27.187L94.8723 21.9622H96.9677L98.2084 27.187C98.429 28.152 98.5117 28.607 98.6496 29.3376H98.7047C98.8288 28.4691 98.9529 27.9039 99.0907 27.2008L100.207 21.9622H102.303L99.7387 31.6812H97.657L96.4301 26.4977C96.2646 25.7671 96.1543 25.2846 95.9476 24.2782H95.8924C95.6856 25.2846 95.5753 25.7671 95.4099 26.4977L94.183 31.6812H92.1013Z" fill="white" />
          <path d="M79.9965 31.6812V23.713H76.8809V21.9622H85.1248V23.713H81.9954V31.6812H79.9965Z" fill="white" />
          <path d="M71.524 31.888C68.7806 31.888 66.9746 29.8753 66.9746 26.8148C66.9746 23.7681 68.7806 21.7554 71.524 21.7554C73.6883 21.7554 75.3151 23.0237 75.577 24.8434L73.5781 25.3121C73.3575 24.1955 72.5855 23.5338 71.4964 23.5338C69.9799 23.5338 69.0287 24.7883 69.0287 26.8148C69.0287 28.8413 69.9799 30.1096 71.4964 30.1096C72.5855 30.1096 73.3575 29.4479 73.5781 28.345L75.577 28.8138C75.3151 30.6335 73.6883 31.888 71.524 31.888Z" fill="white" />
          <path d="M58.459 31.6812V21.9622H65.2003V23.6578H60.4579V25.8636H64.8556V27.5041H60.4579V29.9856H65.2003V31.6812H58.459Z" fill="white" />
          <path d="M47.7656 31.6812V21.9622H49.9576L52.9216 26.9113C53.3765 27.6557 53.6798 28.2899 54.0106 28.993H54.0796L54.0382 26.5115V21.9622H55.9407V31.6812H53.7487L50.771 26.7321C50.3298 25.9876 50.0265 25.3673 49.6819 24.6504H49.6267L49.6681 27.1319V31.6812H47.7656Z" fill="white" />
          <path d="M37.0742 31.6812V21.9622H39.2662L42.2301 26.9113C42.6851 27.6557 42.9884 28.2899 43.3192 28.993H43.3882L43.3468 26.5115V21.9622H45.2493V31.6812H43.0573L40.0795 26.7321C39.6384 25.9876 39.3351 25.3673 38.9905 24.6504H38.9353L38.9767 27.1319V31.6812H37.0742Z" fill="white" />
          <path d="M30.2903 31.888C27.4642 31.888 25.6582 29.8201 25.6582 26.8148C25.6582 23.8233 27.4642 21.7554 30.2903 21.7554C33.1164 21.7554 34.9223 23.8233 34.9223 26.8148C34.9223 29.8201 33.1164 31.888 30.2903 31.888ZM30.2903 30.1096C31.8481 30.1096 32.8682 28.9378 32.8682 26.8148C32.8682 24.6918 31.8481 23.5338 30.2903 23.5338C28.7325 23.5338 27.7123 24.6918 27.7123 26.8148C27.7123 28.9378 28.7325 30.1096 30.2903 30.1096Z" fill="white" />
          <path d="M19.9868 31.888C17.2435 31.888 15.4375 29.8753 15.4375 26.8148C15.4375 23.7681 17.2435 21.7554 19.9868 21.7554C22.1512 21.7554 23.778 23.0237 24.0399 24.8434L22.0409 25.3121C21.8204 24.1955 21.0484 23.5338 19.9593 23.5338C18.4428 23.5338 17.4916 24.7883 17.4916 26.8148C17.4916 28.8413 18.4428 30.1096 19.9593 30.1096C21.0484 30.1096 21.8204 29.4479 22.0409 28.345L24.0399 28.8138C23.778 30.6335 22.1512 31.888 19.9868 31.888Z" fill="white" />
        </svg>
        <span>Aanmelden via Strava</span>
      </a>
    </div>
  );
}
